import { useEffect, useRef, useState } from "react";
import { API_URL, api, formatApiErrorDetail } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { CONTACT_CONSENT_TEXT, SMS_CONSENT_TEXT } from "@/lib/consent";

const MAX_BYTES = 15 * 1024 * 1024;
const PRIORITIES = ["Lower energy costs", "Backup power", "Roof and solar together", "Upgrade my home"];

/**
 * Inline homeowner quote form (homeowner pages): Home address replaces the
 * business property field. Prefillable by the savings calculator via the
 * "homeowner-quote-prefill" window event (bill + optional priority).
 */
export default function HomeownerQuoteForm({ prefillBill = null, prefillPriority = null }) {
  const { config } = useSite();
  const nameRef = useRef(null);
  const successRef = useRef(null);
  const [form, setForm] = useState({
    name: "", street: "", city: "", zip: "", email: "", phone: "", bill: "",
    priority: "Lower energy costs", consent: false, smsConsent: false, company_website: "",
  });
  const [bill, setBillFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [idem, setIdem] = useState("");

  useEffect(() => {
    function prefill(e) {
      const detail = e.detail || {};
      if (detail.bill) setForm((f) => ({ ...f, bill: String(detail.bill) }));
      if (detail.priority) setForm((f) => ({ ...f, priority: detail.priority }));
      document.getElementById("homeowner-quote")?.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => nameRef.current?.focus(), 450);
    }
    window.addEventListener("homeowner-quote-prefill", prefill);
    if (prefillBill) setForm((f) => ({ ...f, bill: String(prefillBill) }));
    if (prefillPriority) setForm((f) => ({ ...f, priority: prefillPriority }));
    return () => window.removeEventListener("homeowner-quote-prefill", prefill);
  }, [prefillBill, prefillPriority]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    const key = `home-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setIdem(key);
    try {
      let uploadIds = [];
      if (bill) {
        if (bill.size > MAX_BYTES) throw new Error("The bill file must be under 15 MB.");
        const fd = new FormData();
        fd.append("file", bill);
        const up = await api.post("/uploads", fd).catch((err) => { throw new Error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); });
        uploadIds = [up.data.id];
      }
      const res = await api.post("/leads", {
        serviceId: "home",
        audience: "home",
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: `${form.street}, ${form.city}, FL ${form.zip}`,
        monthlyBill: form.bill ? Number(form.bill) : null,
        utility: form.zip,
        contextService: "Home solar quote",
        serviceAnswer: form.priority,
        priorities: [form.priority],
        contactConsent: { granted: form.consent, policyVersion: config.policyVersion, recordedAt: new Date().toISOString() },
        smsConsent: { granted: form.smsConsent, text: SMS_CONSENT_TEXT, recordedAt: new Date().toISOString() },
        marketingConsent: false,
        uploadIds,
        attribution: { page: window.location.pathname },
      }, { headers: { "Idempotency-Key": key } }).catch((err) => { throw new Error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed"); });
      setResult(res.data);
      setTimeout(() => successRef.current?.focus(), 30);
    } catch (err) {
      setError(err.message);
      setTimeout(() => successRef.current?.focus(), 30);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section" id="homeowner-quote" style={{ paddingTop: 40, paddingBottom: 60 }} data-testid="homeowner-quote-section">
      <div className="wrap">
        <span className="eyebrow">FREE HOME QUOTE</span>
        <h2 style={{ marginTop: 10 }} data-testid="homeowner-quote-heading">Get your free home solar estimate</h2>
        <p className="muted" style={{ fontSize: 13, maxWidth: 620 }}>
          Tell us about your home and your bill — your free quote confirms real numbers for your roof, your utility and your usage.
        </p>
        {result ? (
          <div className="panel" style={{ maxWidth: 620, marginTop: 18 }} data-testid="homeowner-quote-success" role="status" tabIndex={-1} ref={successRef}>
            <h3 style={{ margin: "0 0 8px" }}>Request received — reference {result.reference}</h3>
            <p className="muted" style={{ fontSize: 13 }}>
              Your free home estimate is saved and the team will follow up. Questions now? Call <a href={config.phoneHref}>{config.phone}</a>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="homeowner-quote-form" style={{ maxWidth: 620, marginTop: 18 }}>
            <div className="twofields">
              <label className="field">
                Full name
                <input required maxLength={100} autoComplete="name" data-testid="home-name" ref={nameRef} value={form.name} onChange={set("name")} />
              </label>
              <label className="field">
                Email
                <input required type="email" maxLength={254} autoComplete="email" data-testid="home-email" value={form.email} onChange={set("email")} />
              </label>
            </div>
            <label className="field">
              Home address — street
              <input required maxLength={200} autoComplete="street-address" data-testid="home-street" placeholder="Street address" value={form.street} onChange={set("street")} />
            </label>
            <div className="twofields">
              <label className="field">
                City
                <input required maxLength={100} autoComplete="address-level2" data-testid="home-city" value={form.city} onChange={set("city")} />
              </label>
              <label className="field">
                ZIP
                <input required maxLength={5} inputMode="numeric" autoComplete="postal-code" data-testid="home-zip" value={form.zip} onChange={set("zip")} />
              </label>
            </div>
            <div className="twofields">
              <label className="field">
                Phone
                <input required type="tel" maxLength={30} autoComplete="tel" data-testid="home-phone" value={form.phone} onChange={set("phone")} />
              </label>
              <label className="field">
                Average monthly electric bill ($)
                <input required type="number" min={30} max={5000} data-testid="home-bill" placeholder="e.g. 220" value={form.bill} onChange={set("bill")} />
              </label>
            </div>
            <label className="field">
              Main priority
              <select data-testid="home-priority" value={form.priority} onChange={set("priority")}>
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label className="upload">
              Add a utility bill (optional, PDF/JPG/PNG under 15 MB)
              <input type="file" accept="application/pdf,image/jpeg,image/png" data-testid="home-bill-upload" onChange={(e) => setBillFile(e.target.files[0] || null)} />
            </label>
            <input className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="company_website" value="" readOnly onChange={() => {}} />
            <label className="check">
              <input type="checkbox" required data-testid="home-consent" checked={form.consent} onChange={set("consent")} />
              <span>
                I agree to be contacted about my request and have reviewed the <a href="/privacy" target="_blank" rel="noreferrer">privacy information</a>.
              </span>
            </label>
            <label className="check" data-testid="home-sms-consent-row">
              <input type="checkbox" data-testid="home-sms-consent" checked={form.smsConsent} onChange={set("smsConsent")} />
              <span>{SMS_CONSENT_TEXT}</span>
            </label>
            <button className="btn" type="submit" disabled={busy} data-testid="home-submit" style={{ marginTop: 16 }}>
              {busy ? "Sending securely…" : "Get my free estimate"} <span>↗</span>
            </button>
            {error && (
              <p className="status" role="alert" style={{ color: "#a33a17" }} data-testid="home-quote-error">
                {error} Or call <a href={config.phoneHref}>{config.phone}</a>.
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
